import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./RelatedProducts.scss";
import { PublicProductList } from "interfaces/IProduct";
import { useBreakpoints } from "hooks/useBreakPoints";
import ProductModal from "../products/ProductModal";
import { fetchRelatedProducts } from "api/auth";
import RelatedProductCard from "./RelatedProductCard";
import { useUserStore } from "../../../../zustand/userStore";
import CustomToast from "components/general/CustomToast";
import { useAuthStore } from "../../../../zustand/loginStore";

interface RelatedProductsProps {
  categoryIds: string;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({ categoryIds }) => {
  const { favoriteProducts, favoritesLoaded, loadFavorites } = useUserStore();
  const { token, isLoggedIn } = useAuthStore();
  const [products, setProducts] = useState<PublicProductList[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationDirection, setAnimationDirection] = useState<
    "left" | "right" | null
  >(null);
  const { isMobile } = useBreakpoints();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<PublicProductList>();
  const [showToast, setShowToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "", show: false, success: true });

  /**
   * Effect hook to fetch and load related products on component mount
   * Makes API call to get products and updates state
   */
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Use the new fetchRelatedProducts function
        const response = await fetchRelatedProducts(categoryIds);
        setProducts(response.products);
      } catch (error) {
        console.error("Error loading related products:", error);
      }
    };

    if (categoryIds) {
      loadProducts();
    }
  }, [categoryIds]);

  /**
   * Fallback effect to load favorites if not loaded and user is logged in
   */
  useEffect(() => {
    if (token && isLoggedIn() && !favoritesLoaded) {
      loadFavorites(token);
    }
  }, [token, isLoggedIn, favoritesLoaded, loadFavorites]);

  /**
   * Handles carousel navigation to next set of products
   * Different behavior for mobile (4 products) and desktop views
   * Includes animation timing logic
   */
  const handleNext = () => {
    if (animationDirection) return;
    setAnimationDirection("left");
    setTimeout(() => {
      setCurrentIndex((prev) =>
        isMobile
          ? (prev + 1) * 4 >= products.length
            ? 0
            : prev + 1
          : (prev + 1) % products.length
      );
      setTimeout(() => {
        setAnimationDirection(null);
      }, 10);
    }, 450);
  };

  /**
   * Handles carousel navigation to previous set of products
   * Different behavior for mobile (4 products) and desktop views
   * Includes animation timing logic
   */
  const handlePrevious = () => {
    if (animationDirection) return;
    setAnimationDirection("right");
    setTimeout(() => {
      setCurrentIndex((prev) =>
        isMobile
          ? prev === 0
            ? Math.floor((products.length - 1) / 4)
            : prev - 1
          : prev === 0
          ? products.length - 1
          : prev - 1
      );
      setTimeout(() => {
        setAnimationDirection(null);
      }, 10);
    }, 450);
  };

  /**
   * Returns array of visible products based on current index
   * Handles both mobile (4 products grid) and desktop (3 products) layouts
   * @returns Array of products with position information
   */
  const getVisibleProducts = () => {
    if (!products.length) return [];

    if (isMobile) {
      const result = [];
      for (let i = 0; i < 4; i++) {
        const index = (currentIndex * 4 + i) % products.length;
        result.push({
          ...products[index],
          position: i + 1,
        });
      }
      return result;
    }
    if (products.length === 1) {
      return [{ ...products[0], position: 2 }]; // Only center position
    }

    if (products.length === 2) {
      return [
        { ...products[0], position: 1.5 }, // Adjusted position for 2 cards
        { ...products[1], position: 2.5 },
      ];
    }
    const result = [];
    for (let i = -1; i < 4; i++) {
      let index = (currentIndex + i + products.length) % products.length;
      result.push({
        ...products[index],
        position: i + 1,
      });
    }
    return result;
  };

  /**
   * Handles touch start event for mobile swipe functionality
   * Stores initial touch position for both X and Y coordinates
   */
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX);
    setTouchStartY(e.touches[0].clientY);
    setIsHorizontalSwipe(false);
  };

  /**
   * Handles touch move event to detect swipe direction
   */
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !touchStartY) return;

    const touchCurrentX = e.touches[0].clientX;
    const touchCurrentY = e.touches[0].clientY;
    const diffX = Math.abs(touchStart - touchCurrentX);
    const diffY = Math.abs(touchStartY - touchCurrentY);

    // Only prevent default if it's clearly a horizontal swipe (not vertical scroll)
    if (diffX > diffY && diffX > 30) {
      setIsHorizontalSwipe(true);
      e.preventDefault();
    }
  };

  /**
   * Handles touch end event for mobile swipe functionality
   * Only triggers navigation if it was a horizontal swipe
   */
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchStartY || !isHorizontalSwipe) {
      setTouchStart(null);
      setTouchStartY(null);
      setIsHorizontalSwipe(false);
      return;
    }

    const touchEnd = e.changedTouches[0].clientX;
    const diffX = touchStart - touchEnd;

    // Only trigger if it's a significant horizontal movement
    if (Math.abs(diffX) > 50) {
      if (diffX > 0) {
        handleNext();
      } else {
        handlePrevious();
      }
    }

    setTouchStart(null);
    setTouchStartY(null);
    setIsHorizontalSwipe(false);
  };

  /**
   * Handles the click event on a product card
   * Opens the product details modal
   *
   * @param product - The selected product to display in modal
   *
   * The function:
   * 1. Updates the selected product state with the clicked product
   * 2. Shows the product details modal by setting showModal to true
   */
  const handleProductClick = (product: PublicProductList) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  /**
   * handles the toast when the favorite toggle endpoint gives feedback
   */
  const handleToast = (message: string, show: boolean, success: boolean) =>
    setShowToast({ message, show, success });

  return (
    <Container
      fluid
      className="related-products-container"
      id="related-products"
    >
      <div
        className={`products-carousel ${isMobile ? "mobile-grid" : ""}`}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {products.length > 0 &&
          getVisibleProducts().map((product) => (
            <RelatedProductCard
              handleToast={handleToast}
              key={`${product.id}-${product.position}-${!!token}`}
              product={product}
              className={`position-${product.position} sliding-${animationDirection}`}
              handleProductClick={handleProductClick}
            />
          ))}
        {products.length > 3 && !isMobile && (
          <>
            <button className="carousel-control prev" onClick={handlePrevious}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                size={window.innerWidth <= 768 ? "1x" : "2x"}
              />
            </button>
            <button className="carousel-control next" onClick={handleNext}>
              <FontAwesomeIcon
                icon={faChevronRight}
                size={window.innerWidth <= 768 ? "1x" : "2x"}
              />
            </button>
          </>
        )}
      </div>
      {selectedProduct && (
        <ProductModal
          show={showModal}
          onHide={() => setShowModal(false)}
          productId={selectedProduct?.id}
        />
      )}
      <CustomToast
        onClose={() => {
          setShowToast({ message: "", show: false, success: true });
        }}
        message={showToast.message}
        show={showToast.show}
        success={showToast.success}
      />
    </Container>
  );
};

export default RelatedProducts;
